import { iframeDataBridge } from '@/iframe/iframeDataBridge.js';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService.js';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const channelCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	logCategory: 'Channel',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.channels.`,
});

export async function getChannels(forceRefresh = false) {
	try {
		if (!forceRefresh) {
			const cached = await channelCache.get('all');
			if (cached) {
				log('info', 'Channel', 'Returning cached channels');
				return cached;
			}

			try {
				const legacyData = await iframeDataBridge.getStorageAsync('CHANNELS');
				if (legacyData) {
					log('info', 'Channel', '📦 Seeding Vue cache from legacy storage (CHANNELS)');
					await channelCache.put('all', legacyData);
					return legacyData;
				}
			} catch (legacyError) {
				log('warn', 'Channel', `⚠️ Legacy storage check failed: ${legacyError.message}`);
			}
		}

		if (iframeDataBridge.isOffline() && !forceRefresh) {
			log('warn', 'Channel', 'Offline: No cached channels available');
			return [];
		}

		const apiUrl = getApiUrl('Distributor/Channels');
		const response = await authenticatedFetch(apiUrl, {}, 'token');

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();

		await channelCache.put('all', data);

		log('info', 'Channel', `Fetched ${data?.length || 0} channels`);
		return data;
	} catch (error) {
		log('error', 'Channel', `Error loading channels: ${error.message}`);

		const cached = await channelCache.get('all');
		if (cached) {
			log('info', 'Channel', 'Returning cached channels after error');
			return cached;
		}

		throw error;
	}
}

export async function getChannelById(id) {
	try {
		const cached = await channelCache.get(id);
		if (cached) {
			return cached;
		}

		if (iframeDataBridge.isOffline()) {
			log('warn', 'Channel', `Offline: Channel ${id} not found in cache`);
			return null;
		}

		const apiUrl = getApiUrl(`Distributor/Channels/${id}`);
		const response = await authenticatedFetch(apiUrl, {}, 'token');

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();

		await channelCache.put(id, data);

		return data;
	} catch (error) {
		log('error', 'Channel', `Error loading channel ${id}: ${error.message}`);
		throw error;
	}
}

export async function clearChannelCache(id = null) {
	if (id) {
		await channelCache.delete(id);
	} else {
		await channelCache.clearAll();
	}
}

export async function cleanOldCacheEntries(maxAgeInDays = 1) {
	await channelCache.cleanOld(maxAgeInDays);
}

export async function getCacheStats() {
	return await channelCache.getStats();
}

cleanOldCacheEntries();
